import { z } from 'zod';
import { AuditSchema, DateOnlySchema, IdSchema, InstantSchema } from './common.ts';
import { MAX_MARKUP_MILLIONTHS } from './cost-code.ts';
import {
  MoneySchema,
  addMoney,
  allocateMoney,
  multiplyMoney,
  sumMoney,
  zeroMoney,
  type Money,
} from '../money.ts';

/**
 * Where a change order has got to.
 *
 * A state machine rather than a pair of booleans, because the states are
 * mutually exclusive and the transitions between them are the thing with
 * rules attached. `expired` is reached by time rather than by anybody, which
 * is why it is a state and not a derived flag: "was it expired when they
 * tried to approve it" has to be answerable afterwards.
 */
export const CO_STATUSES = [
  'draft',
  'released',
  'approved',
  'declined',
  'expired',
  'void',
] as const;
export const CoStatusSchema = z.enum(CO_STATUSES);
export type CoStatus = z.infer<typeof CoStatusSchema>;

/** Who may still change the price. Only a draft. */
export function isEditable(status: CoStatus): boolean {
  return status === 'draft';
}

/**
 * The transitions this product allows.
 *
 * Written as data rather than as a chain of ifs, so the callable, the UI and
 * the tests all read the same table — and so an illegal transition is a
 * missing entry rather than a branch somebody forgot.
 */
export const CO_TRANSITIONS: Readonly<Record<CoStatus, readonly CoStatus[]>> = {
  // Released is the act of showing it to the owner. Void is for a change
  // order raised by mistake, which is different from one the owner declined.
  draft: ['released', 'void'],
  // An owner approves or declines; the office can do either on their behalf
  // (a phone call), and can pull it back to draft to correct it.
  released: ['approved', 'declined', 'expired', 'draft', 'void'],
  // An approved change order is not editable, but it can be voided — work
  // cancelled after approval happens, and the record has to be able to say
  // so rather than being deleted.
  approved: ['void'],
  // A declined one can be reworked and released again, which is the whole
  // point of recording the reason.
  declined: ['draft', 'void'],
  expired: ['draft', 'void'],
  void: [],
};

export function canTransition(from: CoStatus, to: CoStatus): boolean {
  return CO_TRANSITIONS[from].includes(to);
}

/**
 * One line of a change order.
 *
 * `unitCost` is the builder's cost and `markupPercent` is what is added to
 * reach the owner's price. Both are stored; the owner price is **derived**.
 * A stored total is a total that eventually disagrees with its lines, and
 * the disagreement is discovered by a customer.
 */
export const CoLineSchema = z.object({
  id: IdSchema,
  description: z.string().trim().min(1).max(500),
  /** Thousandths, so 2.5 hours is 2500 and nothing rounds. */
  quantityThousandths: z.number().int().positive(),
  unit: z.string().trim().max(20).optional(),
  unitCost: MoneySchema,
  /**
   * Markup in **millionths**: 20% is 200_000, 100% is 1_000_000.
   *
   * The name is wrong and the arithmetic is right. One of these units is
   * 0.0001%, so a basis point — 0.01% — is a hundred of them, and the
   * field is a hundred times what it says it is. `markupPercent` in the
   * UI converts with `× 10_000` and `/ 10_000`, and `lineMarkup` divides
   * by 1_000_000, which is the same scale.
   *
   * This comment used to read "hundredths of a percent: 20% is 2000",
   * which is a hundredfold error in the direction that matters: somebody
   * following it would write 2000 for 20% and quote an owner a 0.2%
   * markup. Renaming the field is a data-model change needing a migration
   * of existing documents, so it is raised as P5-00 rather than done
   * here; `percentFromMarkup` and `markupFromPercent` below are the
   * conversion in one place, and a test pins the scale so the comment
   * cannot drift from the code again.
   *
   * The ceiling of 1_000_000 is 100% markup. Nothing in the product needs
   * more and a typed 10_000% is more likely a mistake than an intention.
   */
  markupMillionths: z.number().int().min(0).max(MAX_MARKUP_MILLIONTHS).default(0),
  costCodeId: IdSchema.optional(),
  /** Whether the owner sees this line at all, or only the total. */
  showToOwner: z.boolean().default(true),
});
export type CoLine = z.infer<typeof CoLineSchema>;

export const CO_PRICE_MODES = ['lineItems', 'flatFee'] as const;
export const CoPriceModeSchema = z.enum(CO_PRICE_MODES);
export type CoPriceMode = z.infer<typeof CoPriceModeSchema>;

export const CoApprovalSchema = z.object({
  /** The uid who approved, or the office member who did it on their behalf. */
  by: IdSchema,
  at: InstantSchema,
  /** Set when the office approved for the owner, with who they spoke to. */
  onBehalfOf: IdSchema.optional(),
  /** Storage path of the drawn signature, when there is one. */
  signaturePath: z.string().min(1).optional(),
  /** The owner's own words, which matter most when they declined. */
  comment: z.string().max(2_000).optional(),
  /** The disclaimer text as it stood when they agreed to it. */
  disclaimer: z.string().max(5_000).optional(),
});
export type CoApproval = z.infer<typeof CoApprovalSchema>;

export const ChangeOrderSchema = AuditSchema.extend({
  id: IdSchema,
  companyId: IdSchema,
  jobId: IdSchema,
  /**
   * The number people say out loud: `0007`.
   *
   * Sequential per job and allocated by a function, because a sequence
   * cannot be client-generated without two change orders claiming `0007`.
   * Editable while the change order is a draft, which is why uniqueness has
   * to be checked on an edit as well as on a create.
   */
  number: z.string().trim().min(1).max(20),
  title: z.string().trim().min(1).max(200),
  /** Shown to the owner. Plain text until there is a sanitiser (P3-06d). */
  description: z.string().max(20_000).default(''),
  status: CoStatusSchema,
  priceMode: CoPriceModeSchema.default('lineItems'),
  lines: z.array(CoLineSchema).default([]),
  /** Used instead of the lines when `priceMode` is `flatFee`. */
  flatFeePrice: MoneySchema.optional(),
  /** Whether the owner sees the line breakdown or only the total. */
  showLinesToOwner: z.boolean().default(true),
  approvalDeadline: DateOnlySchema.optional(),
  releasedAt: InstantSchema.optional(),
  approval: CoApprovalSchema.optional(),
  /** Why it was declined or voided, which is the useful part of either. */
  reason: z.string().max(2_000).optional(),
  ownerLastViewedAt: InstantSchema.optional(),
  tagIds: z.array(IdSchema).default([]),
  linkedRecords: z.array(z.object({ kind: z.string(), id: IdSchema })).default([]),
  linkedKeys: z.array(z.string().min(1)).default([]),
});
export type ChangeOrder = z.infer<typeof ChangeOrderSchema>;

/**
 * The scale of `markupMillionths`, in one place.
 *
 * Two call sites multiplied and divided by `10_000` inline, which is how
 * the doc comment on the field came to disagree with them by a hundred
 * without anything failing. Anything in Phase 5 that takes a markup from a
 * person uses these rather than its own literal.
 */
export const MARKUP_UNITS_PER_PERCENT = 10_000;

/** A typed percentage as stored units: 17.5 becomes 175_000. */
export function markupFromPercent(percent: number): number {
  return Math.round(percent * MARKUP_UNITS_PER_PERCENT);
}

/** Stored units as a percentage, for display: 175_000 becomes 17.5. */
export function percentFromMarkup(units: number): number {
  return units / MARKUP_UNITS_PER_PERCENT;
}

/** A line's builder cost: quantity times unit cost, with no markup. */
export function lineCost(line: Pick<CoLine, 'quantityThousandths' | 'unitCost'>): Money {
  // Thousandths, so a quantity of 2.5 is exact and the multiplication is by
  // an integer — `multiplyMoney` rounds once, at the end, rather than
  // compounding a float per line.
  return multiplyMoney(line.unitCost, line.quantityThousandths / 1000);
}

/** A line's markup in money, derived from its cost. */
export function lineMarkup(
  line: Pick<CoLine, 'quantityThousandths' | 'unitCost' | 'markupMillionths'>,
): Money {
  return multiplyMoney(lineCost(line), line.markupMillionths / 1_000_000);
}

/** A line's owner price: cost plus markup. */
export function linePrice(
  line: Pick<CoLine, 'quantityThousandths' | 'unitCost' | 'markupMillionths'>,
): Money {
  return addMoney(lineCost(line), lineMarkup(line));
}

export interface CoTotals {
  readonly cost: Money;
  readonly markup: Money;
  readonly price: Money;
}

/**
 * What the change order comes to.
 *
 * Always derived, never read from a field. In flat-fee mode the owner price
 * is what somebody typed and the cost is still the sum of any internal
 * lines, because a flat fee is a price decision and the job still has to
 * know what it cost.
 */
export function coTotals(
  order: Pick<ChangeOrder, 'priceMode' | 'lines' | 'flatFeePrice'>,
): CoTotals {
  const currency = order.lines[0]?.unitCost.currency ?? order.flatFeePrice?.currency;
  const cost = sumMoney(order.lines.map(lineCost), currency);

  if (order.priceMode === 'flatFee') {
    const price = order.flatFeePrice ?? zeroMoney(currency);
    return {
      cost,
      markup: { amount: price.amount - cost.amount, currency: price.currency },
      price,
    };
  }

  const price = sumMoney(order.lines.map(linePrice), currency);
  return { cost, markup: { amount: price.amount - cost.amount, currency: price.currency }, price };
}

/**
 * The lines an owner may see, and the price they add up to.
 *
 * Two separate questions. A line can be hidden from the owner while still
 * counting towards what they are charged — that is what `showLinesToOwner`
 * false means — so the price shown must come from `coTotals`, not from the
 * visible lines. Deriving it from the visible lines is how an owner is
 * quoted a total that does not match the sum in front of them.
 */
export function ownerVisibleLines(
  order: Pick<ChangeOrder, 'lines' | 'showLinesToOwner'>,
): CoLine[] {
  if (!order.showLinesToOwner) return [];
  return order.lines.filter((line) => line.showToOwner);
}

/**
 * Split a flat fee across cost codes by their share of the cost.
 *
 * Uses `allocateMoney`, which hands out remainder cents, so the parts always
 * re-sum to the whole. A flat fee that does not reconcile to the budget is a
 * flat fee somebody has to explain every month.
 */
export function allocateFlatFee(order: Pick<ChangeOrder, 'lines' | 'flatFeePrice'>): Money[] {
  const fee = order.flatFeePrice;
  if (!fee || order.lines.length === 0) return [];
  return allocateMoney(
    fee,
    order.lines.map((line) => Math.max(lineCost(line).amount, 0)),
  );
}

/** Whether a released change order has run past its deadline. */
export function hasExpired(
  order: Pick<ChangeOrder, 'status' | 'approvalDeadline'>,
  today: string,
): boolean {
  if (order.status !== 'released') return false;
  if (!order.approvalDeadline) return false;
  // On the deadline it is still open. A deadline of the 19th means the 19th
  // is the last day somebody can answer, which is how anybody reads it.
  return order.approvalDeadline < today;
}

/** The next number in a job's sequence, formatted the way people say it. */
export function formatCoNumber(sequence: number): string {
  return String(sequence).padStart(4, '0');
}

/** The widest sequence `formatCoNumber` can render without growing. */
export const CO_NUMBER_MAX = 9999;

/**
 * A change-order number somebody typed, as a sequence.
 *
 * `undefined` for anything that is not one, which is every answer the
 * caller has to refuse. Four digits and nothing else, and the reason is
 * not tidiness: `listChangeOrders` orders by the `number` **string**, so a
 * number that is not fixed width sorts in the wrong place — `'12'` lands
 * after `'0100'` because `'1' > '0'`. The padding is what makes a text
 * ordering a numeric one, and a number with a letter in it (`0012-A`)
 * could not be compared with the counter at all.
 */
export function parseCoNumber(raw: string): number | undefined {
  const trimmed = raw.trim();
  if (!/^\d{1,4}$/.test(trimmed)) return undefined;
  const sequence = Number.parseInt(trimmed, 10);
  // Zero is not a change order anybody has. The counter starts at one.
  return sequence >= 1 && sequence <= CO_NUMBER_MAX ? sequence : undefined;
}

/**
 * What an owner is allowed to see of a change order.
 *
 * A **separate document**, not a filtered read. Firestore rules are
 * all-or-nothing per document: there is no way to let somebody read a
 * change order while hiding `unitCost` and `markupMillionths` on its
 * lines. Letting the owner read the real document would hand them the
 * builder's cost on every line, which is the one thing
 * `docs/PERMISSIONS.md` says must never happen — and the Phase 4 exit
 * criterion says so in those words.
 *
 * So a trigger derives this and writes it where the owner can read it, and
 * nothing in it is a cost. `ownerLine` carries a price and no cost at all,
 * which is a stronger guarantee than a rule could express: the cost is not
 * in the document to leak.
 */
export const OwnerCoLineSchema = z.object({
  id: IdSchema,
  description: z.string(),
  quantityThousandths: z.number().int(),
  unit: z.string().optional(),
  /** What the owner pays for this line. No cost, no markup. */
  price: MoneySchema,
});
export type OwnerCoLine = z.infer<typeof OwnerCoLineSchema>;

export const OwnerChangeOrderSchema = z.object({
  id: IdSchema,
  companyId: IdSchema,
  jobId: IdSchema,
  number: z.string(),
  title: z.string(),
  description: z.string(),
  status: CoStatusSchema,
  price: MoneySchema,
  lines: z.array(OwnerCoLineSchema).default([]),
  approvalDeadline: DateOnlySchema.optional(),
  releasedAt: InstantSchema.optional(),
  approval: CoApprovalSchema.optional(),
  reason: z.string().optional(),
  updatedAt: InstantSchema,
});
export type OwnerChangeOrder = z.infer<typeof OwnerChangeOrderSchema>;

/**
 * Whether an owner should have a mirror at all.
 *
 * A draft has not been shown to anybody: mirroring one would put a price in
 * front of a customer before the office decided to. Everything from
 * `released` onwards is theirs to see, including `declined` and `void` —
 * they need to know what happened to something they were asked about.
 */
export function isOwnerVisible(status: CoStatus): boolean {
  return status !== 'draft';
}

/** Derive the owner's view. Costs are absent, not hidden. */
export function ownerChangeOrderFrom(order: ChangeOrder): OwnerChangeOrder {
  const totals = coTotals(order);
  return {
    id: order.id,
    companyId: order.companyId,
    jobId: order.jobId,
    number: order.number,
    title: order.title,
    description: order.description,
    status: order.status,
    // From every line, not only the visible ones: a line hidden from the
    // owner still counts towards what they are charged, and a total that
    // did not match would be a quote nobody could reconcile.
    price: totals.price,
    lines: ownerVisibleLines(order).map((line) => ({
      id: line.id,
      description: line.description,
      quantityThousandths: line.quantityThousandths,
      ...(line.unit ? { unit: line.unit } : {}),
      price: linePrice(line),
    })),
    ...(order.approvalDeadline ? { approvalDeadline: order.approvalDeadline } : {}),
    ...(order.releasedAt ? { releasedAt: order.releasedAt } : {}),
    ...(order.approval ? { approval: order.approval } : {}),
    ...(order.reason ? { reason: order.reason } : {}),
    updatedAt: order.updatedAt,
  };
}
